import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CCOLinkable from "../../fundamentals/CCOLinkable";
import {getAltText, getPictureUri} from "../../fundamentals/CCOPicture";
import {CmPictureType} from "../../types";
import {buildCdnUrl, hasFallbackUrl, isCdnEnabled} from "../../../utility/cdnPictureUtil";
import {Crops} from "../../../../packages/graphql-layer/src/__generated__/crops";
import {usePreviewContext} from "../../../context/PreviewContext";

export interface LinkedImageItem {
    picture?: CmPictureType | null;
    teaserTarget?: { type?: string; selfUrl?: string | null } | null;
    type?: string;

    [key: string]: unknown;
}

export interface Props {
    hasTile?: boolean;
    item: {
        id?: string;
        type?: string;
        picture?: CmPictureType | null;
        teaserTarget?: { type?: string; selfUrl?: string | null } | null;
        [key: string]: unknown
    };
}

function isPicture(item: Props["item"]): item is CmPictureType {
    return item.type === "CMPicture";
}

function getLinkedImageData(item: Props["item"]): {
    picture: CmPictureType | null | undefined;
    hasLink: boolean;
    isExternal: boolean;
    linkItem: LinkedImageItem | null
} {
    if (isPicture(item)) {
        return {picture: item, hasLink: false, isExternal: false, linkItem: null};
    }
    const teasable = item as LinkedImageItem;
    const hasLink = teasable.teaserTarget != null && teasable.teaserTarget.selfUrl != null;
    const isExternal = hasLink && teasable.teaserTarget?.type === "CMExternalLink";
    return {picture: teasable.picture as CmPictureType | undefined, hasLink, isExternal, linkItem: teasable};
}

function getOriginalPictureUri(picture: CmPictureType, isPreview: boolean): string | null | undefined {
    if (isCdnEnabled(picture)) {
        return buildCdnUrl(picture, "original", "", isPreview);
    }
    if (hasFallbackUrl(picture)) {
        return getPictureUri(
            String(Crops.landscape_ratio16x9.w585h329.width),
            isPreview,
            picture,
            Crops.landscape_ratio16x9.cropName,
        );
    }
    return picture.data?.uri;
}

const outlineStyles = {
    outline: "3px solid transparent",
    outlineOffset: "-3px",
    position: "relative",
    transition: "outline-color 0.2s ease",
};

const tileFrameStyles = {
    ...outlineStyles,
    alignItems: "center",
    backgroundColor: "common.white",
    boxSizing: "border-box",
    display: "flex",
    height: {xs: "154px", md: "155px"},
    justifyContent: "center",
    mx: "auto",
    padding: "34px",
    width: {xs: "275px", md: "277px"},
};

const plainFrameStyles = {
    ...outlineStyles,
    width: "100%",
};

const interactiveStyles = {
    display: "block",
    cursor: "pointer",
    "&:hover .linked-image-frame, &:focus-within .linked-image-frame": {
        outlineColor: "primary.main",
    },
    "&:hover .MuiChip-root, &:focus-within .MuiChip-root": {
        opacity: 1,
    },
};

const tileImageStyles = {
    display: "block",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
    width: "100%",
};

const plainImageStyles = {
    display: "block",
    height: "auto",
    width: "100%",
};

function C092LinkedImage({hasTile = true, item}: Props) {
    const {isPreview} = usePreviewContext();
    const {picture, hasLink, isExternal, linkItem} = getLinkedImageData(item);
    const imageUri = picture ? getOriginalPictureUri(picture, isPreview) : undefined;

    if (!picture || !imageUri) {
        return null;
    }

    const image = (
        <Box className="linked-image-frame" sx={hasTile ? tileFrameStyles : plainFrameStyles}>
            <Box
                alt={getAltText(picture.alt, picture.title)}
                component="img"
                loading="lazy"
                src={imageUri}
                sx={hasTile ? tileImageStyles : plainImageStyles}
                title={picture.title ?? ""}
            />
            {isExternal && (
                <Chip
                    label="Externe Website"
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        borderRadius: 1,
                        opacity: 0,
                        transition: "opacity 0.2s ease",
                    }}
                />
            )}
        </Box>
    );

    if (hasLink && linkItem) {
        return (
            <Box sx={interactiveStyles}>
                <CCOLinkable target={linkItem} type={linkItem.type}>
                    {image}
                </CCOLinkable>
            </Box>
        );
    }

    return image;
}

export default C092LinkedImage;
